import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_hygiene-landing-1/artifacts/ctguk1za_e7fcb3bb-e3d3-4b55-b1a7-3e8387598f1e.jpeg";

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const navItems = [
    { label: "Our story", section: "about" },
    { label: "Collective", section: "blogs" },
    { label: "Workshops", section: "products" },
    { label: "Programs", section: "hero" },
    { label: "Inquiries", section: "contact" }
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    setMobileOpen(false);
  };

  return (
    <motion.header
      initial={{ y: -30, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/40 backdrop-blur-lg border-b border-white/10' : 'bg-transparent'
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-3 md:py-4 flex items-center justify-between">
        {/* Logo - top left */}
        <button
          onClick={() => scrollTo('home')}
          className="flex items-center gap-2 group"
          data-testid="header-logo"
        >
          <div className="bg-white rounded-xl p-1.5 shadow-lg group-hover:shadow-xl transition-shadow">
            <img
              src={LOGO_URL}
              alt="The Hygiene Company"
              className="h-8 sm:h-10 md:h-12 w-auto object-contain"
            />
          </div>
          <span className="hidden sm:block text-white font-semibold text-xs md:text-sm drop-shadow-lg leading-tight">
            The Hygiene<br />Company
          </span>
        </button>

        {/* Desktop Nav */}
        <nav className="hidden md:block">
          <ul className="flex items-center gap-4 lg:gap-8 bg-[#87CEEB]/80 backdrop-blur-md rounded-full px-5 lg:px-8 py-2.5 shadow-lg">
            {navItems.map((item, i) => (
              <li key={i}>
                <button
                  data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  onClick={() => scrollTo(item.section)}
                  className="text-white/95 hover:text-white text-xs lg:text-sm font-medium transition-colors whitespace-nowrap"
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </nav>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileOpen(!mobileOpen)}
          className="md:hidden p-2 bg-white/15 backdrop-blur-md border border-white/30 rounded-lg text-white"
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile Nav */}
      {mobileOpen && (
        <motion.nav
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          exit={{ height: 0, opacity: 0 }}
          className="md:hidden bg-black/70 backdrop-blur-lg border-t border-white/10"
        >
          <ul className="px-6 py-4 space-y-3">
            {navItems.map((item, i) => (
              <li key={i}>
                <button
                  onClick={() => scrollTo(item.section)}
                  className="text-white/90 hover:text-white text-sm font-medium w-full text-left py-2 transition-colors"
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  {item.label}
                </button>
              </li>
            ))}
          </ul>
        </motion.nav>
      )}
    </motion.header>
  );
};
